import { useEffect, useState } from "react";

/**
 * useAutoPiP — Mobile Out-of-App Picture-in-Picture for HTML5 <video>.
 *
 * When the user backgrounds the browser (switch app, lock screen,
 * open another tab), this hook automatically pops the playing
 * <video> element into a system-level floating PiP window so playback
 * continues visibly on top of whatever the user is doing.
 *
 * When they come back to the tab, the video returns to its original
 * place (the browser handles that for us — `leavepictureinpicture`
 * fires but we don't need to do anything).
 *
 * Why a hook instead of putting this on every player:
 *   • Identical behavior for every <video> in the app.
 *   • Single source of truth — easier to tune (e.g. add a settings
 *     toggle later to disable auto-PiP).
 *   • Cleans up its own listeners reliably on unmount.
 *
 * Args:
 *   videoRef     – React ref to a HTML5 <video> element.
 *   active       – Boolean; only arms the listeners when true (e.g.
 *                  while the player modal is open). Saves us from
 *                  popping random hidden videos into PiP.
 *   enableAuto   – Optional. Default true. Set false to keep manual
 *                  PiP only (the returned `requestPiP` still works).
 *
 * Returns:
 *   { supported, inPiP, requestPiP, exitPiP } — for wiring a manual
 *   "Pop out" button next to the player controls.
 */
export default function useAutoPiP({ videoRef, active, enableAuto = true } = {}) {
  const [supported, setSupported] = useState(false);
  const [inPiP, setInPiP] = useState(false);

  // Feature detect once on mount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    setSupported(!!document.pictureInPictureEnabled);
  }, []);

  // Track PiP enter/leave so consumers can highlight the toggle.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const v = videoRef?.current;
    if (!v) return;
    const onEnter = () => setInPiP(true);
    const onLeave = () => setInPiP(false);
    v.addEventListener("enterpictureinpicture", onEnter);
    v.addEventListener("leavepictureinpicture", onLeave);
    return () => {
      v.removeEventListener("enterpictureinpicture", onEnter);
      v.removeEventListener("leavepictureinpicture", onLeave);
    };
  }, [videoRef, active]);

  // Auto-PiP on visibilitychange (browser backgrounded / tab hidden).
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!active || !enableAuto) return;
    const onHidden = async () => {
      if (document.visibilityState !== "hidden") return;
      const v = videoRef?.current;
      if (!v) return;
      // Only pop into PiP if there is actual playback happening.
      if (v.paused || v.ended) return;
      if (document.pictureInPictureElement) return; // already PiP
      // Some browsers expose `disablePictureInPicture` opt-out.
      if (v.disablePictureInPicture) return;
      try {
        await v.requestPictureInPicture();
      } catch (_) {
        /* user denied / browser refused — fail silently. */
      }
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [videoRef, active, enableAuto]);

  const requestPiP = async () => {
    const v = videoRef?.current;
    if (!v) return;
    try {
      if (document.pictureInPictureElement === v) {
        await document.exitPictureInPicture();
      } else {
        await v.requestPictureInPicture();
      }
    } catch (_) {
      /* silent */
    }
  };

  const exitPiP = async () => {
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
    } catch (_) {}
  };

  return { supported, inPiP, requestPiP, exitPiP };
}
